import { RouteTab } from 'custom_hooks/data_hooks/data_source/useRouteTabs';
import { ContentTabConfig } from 'custom_hooks/data_hooks/data_source/useTabs';
import { NotFound } from 'pages/NotFound';
import { useMemo } from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';

const getDefaultUrl = (
    routeTabs: RouteTab<ContentTabConfig>[],
    redirectDefault: string | boolean = true
) => {
    if (typeof redirectDefault === 'string') {
        const tab = routeTabs.find(({ id }) => id === redirectDefault);
        if (tab === undefined) {
            throw new Error(`Tab with id '${redirectDefault}' not found`);
        }
        return tab.url;
    }
    if (redirectDefault) {
        if (routeTabs.length === 0) {
            throw new Error('Tab list is empty');
        }
        return routeTabs[0].url;
    }
    return undefined;
};

export type RouteTabsProps = Readonly<{
    /**
     * Tab configuration
     */
    routeTabs: RouteTab<ContentTabConfig>[];
    /**
     * Enables redirecting to a default tab if not supplied in URL (defaults to `true`).
     *
     * * When `false`, auto-redirection is disabled.
     * * When tab id (`string`), defaults to the tab with the supplied id.
     * * When `true`, defaults to the first tab.
     */
    redirectDefault?: string | boolean;
    /**
     * Enables redirecting to the specified `redirectDefault` on NotFound.
     */
    defaultOnNotFound?: boolean;
}>;

/**
 * Renders the content for the current tab based on route tab config and the current URL.
 * Also handles redirecting to a default tab when tab is missing from the URL, or redirecting
 * to the not found page when an invalid tab is supplied in the URL.
 *
 * Routing Rules:
 *
 * * If no tab is found in the URL, will redirect to the default route.
 *   *  If the default route is disabled, no content will be displayed.
 * * If a valid tab id is found in the URL, its associated content will be rendered.
 * * If an invalid tab id is found in the URL, redirects to "not found" page unless
 *   `defaultOnNotFound` is specified, in which case it redirects to the default page.
 */
export function TabRouter({
    routeTabs,
    redirectDefault,
    defaultOnNotFound,
}: RouteTabsProps) {
    const { path, url } = useRouteMatch();

    const defaultUrl = useMemo(() => getDefaultUrl(routeTabs, redirectDefault), [
        redirectDefault,
        routeTabs,
    ]);

    return (
        <Switch>
            {defaultUrl !== undefined && (
                <Route path={`${path}`} exact>
                    <Redirect to={defaultUrl} />
                </Route>
            )}
            {routeTabs.map((route) => (
                <Route key={route.id} path={route.url}>
                    {route.content}
                </Route>
            ))}
            <Route path={`${url}/*`} exact>
                {defaultOnNotFound && defaultUrl !== undefined ? (
                    <Redirect to={defaultUrl} />
                ) : (
                    <NotFound />
                )}
            </Route>
        </Switch>
    );
}
